import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import JsBarcode from 'jsbarcode';

// ─── Barcode ──────────────────────────────────────────────────────────────────

const Barcode: React.FC<{ value: string }> = ({ value }) => {
  const ref = useRef<SVGSVGElement>(null);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (!ref.current || !value) return;
    setInvalid(false);
    try {
      JsBarcode(ref.current, value, {
        format: 'CODE128',
        width: 2,
        height: 60,
        displayValue: true,
        fontSize: 12,
        margin: 6,
      });
      // JsBarcode sets a fixed px width attribute — override so CSS can scale it
      ref.current.setAttribute('width', '100%');
    } catch {
      setInvalid(true);
    }
  }, [value]);

  if (invalid) {
    return (
      <div
        style={{
          border: '1px dashed #f44336',
          borderRadius: 4,
          padding: '8px 12px',
          color: '#f44336',
          fontSize: 12,
        }}
      >
        Cannot encode: <strong>{value}</strong>
      </div>
    );
  }

  return <svg ref={ref} style={{ maxWidth: '100%', display: 'block' }} />;
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const BarcodePrint: React.FC = () => {
  const location = useLocation();
  const queryValues = new URLSearchParams(location.search).get('codes') ?? '';
  const [input, setInput] = useState(queryValues);

  const codes = input
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; }
        }
        @page { margin: 0.5in; }
      `}</style>

      <div style={{ fontFamily: 'sans-serif', maxWidth: 960, margin: '0 auto', padding: 16 }}>
        {/* Controls */}
        <div className="no-print" style={{ marginBottom: 20 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 12,
            }}
          >
            <div style={{ flex: 1 }}>
              <label
                htmlFor="codes-input"
                style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}
              >
                Comma-separated values
              </label>
              <textarea
                id="codes-input"
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="ABC123, DEF456, BIN-A-01-1, ..."
                rows={3}
                style={{
                  width: '100%',
                  fontSize: 14,
                  padding: '8px 10px',
                  border: '1px solid #ccc',
                  borderRadius: 6,
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  fontFamily: 'monospace',
                }}
              />
              {codes.length > 0 && (
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  {codes.length} barcode{codes.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
            <button
              onClick={() => window.print()}
              disabled={codes.length === 0}
              style={{
                marginTop: 22,
                padding: '8px 24px',
                fontSize: 15,
                fontWeight: 600,
                background: codes.length === 0 ? '#ccc' : '#1976d2',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: codes.length === 0 ? 'default' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Print
            </button>
          </div>
        </div>

        {/* Barcodes */}
        {codes.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 12,
            }}
          >
            {codes.map((code, i) => (
              <div
                key={`${code}-${i}`}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  padding: '10px 12px',
                  background: '#fff',
                  pageBreakInside: 'avoid',
                  breakInside: 'avoid',
                }}
              >
                <Barcode value={code} />
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default BarcodePrint;
